import React, { FC, Fragment, useEffect, useState } from 'react';
import { SetStateAction } from 'jotai';
import { Image, ImageProps } from 'react-bootstrap';
import { createPortal } from 'react-dom';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Pagination } from 'swiper/modules';
import 'swiper/css/pagination';
import useViewports from '../../../Hooks/useViewports';
import useValidImages from '../../../Hooks/useValidImages';
import { OnlineshopProductImages } from '../../../../Interface/OnlineshopProductImages';
import Popup from '../../Popup/Popup';
//@ts-ignore
import styles from './ProductSizeGallery.module.scss?module';

type ProductSizeGalleryPropT = {
  showImageGalerie: boolean;
  setShowImageGalerie: React.Dispatch<SetStateAction<boolean>>;
  images: OnlineshopProductImages;
};

const ProductSizeGallery = ({
  showImageGalerie,
  setShowImageGalerie,
  images,
}: ProductSizeGalleryPropT) => {
  const { xs, sm } = useViewports();
  const validImages = useValidImages(images);

  return createPortal(
    <Popup
      size={'xl'}
      dialogClassName={styles.GalerieModal}
      contentClassName={styles.GalerieContent}
      style={{ border: '0px' }}
      centered
      show={showImageGalerie}
      onHide={() => {
        setShowImageGalerie(false);
      }}
    >
      {showImageGalerie &&
        (xs || sm ? (
          <ImageGalerieMobile images={validImages} />
        ) : (
          <ImageGalerieDesk images={validImages} />
        ))}
    </Popup>,
    document.body
  );
};

export default ProductSizeGallery;

const ImageGalerieMobile: FC<{ images: OnlineshopProductImages }> = ({
  images,
}) => {
  const galerieImages = [
    images.front_image_url,
    images.nutrition_facts_image_url,
    ...(images.sell_sheets.map((item) => {
      return item.image_url;
    }) ?? []),
  ];
  return (
    <>
      <Popup.Header closeButton />
      <Popup.Body className={styles.ModalBodyMobile}>
        <Swiper
          navigation={false}
          pagination={{ clickable: true }}
          spaceBetween={5}
          modules={[Pagination]}
          autoHeight={true}
        >
          {galerieImages
            .filter((imgSrc) => imgSrc && imgSrc.length > 0)
            .map((img, ind) => {
              return (
                <SwiperSlide key={ind}>
                  <Image width={'100%'} src={img} />
                </SwiperSlide>
              );
            })}
        </Swiper>
      </Popup.Body>
    </>
  );
};

type DefaultMainImageT = {
  name: string;
  src: string;
};

const ImageGalerieDesk: FC<{ images: OnlineshopProductImages }> = ({
  images,
}) => {
  const defaultMainImage = {
    name: 'front_image_url',
    src: images.front_image_url || null,
  };

  const [mainImage, setMainImage] = useState<DefaultMainImageT>({
    name: defaultMainImage.name,
    src: defaultMainImage.src || '',
  });

  useEffect(() => {
    setMainImage({
      name: defaultMainImage.name,
      src: defaultMainImage.src || '',
    });
  }, [images]);

  const handleUpdateMainImage = ({ name, src }: DefaultMainImageT) => {
    if (!images || src === mainImage.src) return;
    setMainImage({ name: name, src: src });
  };

  return (
    <Popup.Body className={styles.ModalBodyDesk}>
      <div className={'p-2 d-flex flex-column gap-2'}>
        {images?.front_image_url?.length > 0 && (
          <PreviewImage
            style={{ objectFit: 'contain', objectPosition: 'center' }}
            src={images.front_image_url}
            onClick={() => {
              handleUpdateMainImage({
                name: 'front_image_url',
                src: images.front_image_url,
              });
            }}
            alt="product_front_image"
          />
        )}

        {images?.nutrition_facts_image_url &&
          images?.nutrition_facts_image_url?.length > 0 && (
            <PreviewImage
              className={styles.PreviewImageNF}
              src={images.nutrition_facts_image_url}
              onClick={() =>
                handleUpdateMainImage({
                  name: 'nutrition_facts_image_url',
                  src: images.nutrition_facts_image_url || defaultMainImage.src,
                })
              }
              alt="nutrition_facts_image"
            />
          )}
        {images.sell_sheets.length > 0 &&
          images.sell_sheets.map((img, ind) => {
            return (
              <Fragment key={img.image_url}>
                {img.image_url && img.image_url.length > 0 && (
                  <PreviewImage
                    key={img.image_url}
                    src={img.image_url}
                    alt={`image-${ind}`}
                    onClick={() =>
                      handleUpdateMainImage({
                        name: `image_url-${ind + 1}`,
                        src: img.image_url || defaultMainImage.name,
                      })
                    }
                    className={styles.PreviewImageSL}
                  />
                )}
              </Fragment>
            );
          })}
      </div>

      {/* main Image Container  */}
      <div className={`${styles.MainImageContainer} p-2`}>
        {mainImage.src ? (
          <Image
            loading="lazy"
            style={{
              maxWidth:
                mainImage.name === 'nutrition_facts_image_url'
                  ? '600px'
                  : undefined,
            }}
            src={mainImage.src}
          />
        ) : null}
      </div>
    </Popup.Body>
  );
};

type PreviewImagePropsT = {
  onClick: () => void;
  className?: string;
} & ImageProps;

const PreviewImage: FC<PreviewImagePropsT> = ({
  onClick,
  className,
  ...props
}) => {
  return (
    <Image
      {...props}
      className={`${styles.PreviewImage} ${className}`}
      onClick={onClick}
    />
  );
};
